import { Box, Grid, Typography } from "@mui/material";
import { OLPaper } from "OLPaper";
import { useEvents } from "api/events";
import { useAppSelector } from "app/hooks";
import { OLMenuButton } from "components/FormControls/OLMenuButton";
import { ReactComponent as FilePlus03Icon } from "components/Icons/file-plus-03.svg";
import { FieldArray } from "formik";
import { useMessage } from "hooks/intl";
import { memo, useCallback, useMemo } from "react";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import { FETierSetFormat } from "types/tierSets";
import { useStyles } from "../TierSetForm.styles";
import { Condition } from "./Condition";

export const TierSetConditions = memo(() => {
  const classes = useStyles();
  const { message } = useMessage();
  const { currency } = useAppSelector(selectCurrentStoreData);
  const { useListOfCustomEventsSchema } = useEvents();
  const { data: customEvents } = useListOfCustomEventsSchema();

  const tierSetConditions: {
    label: string;
    attribute: string;
  }[] = useMemo(
    () => [
      { label: message({ id: "activeUnits" }), attribute: "activeUnits" },
      {
        label: message({ id: "totalEarnedUnitsConst" }),
        attribute: "totalEarnedUnits",
      },
      {
        label: message({ id: "totalSpendingWithCurrency" }, { currency }),
        attribute: "totalSpending",
      },
      {
        label: message({ id: "monthsSinceJoiningTheProgram" }),
        attribute: "monthsSinceJoiningProgram",
      },
      {
        label: message({ id: "cumulativeEarnedUnits" }),
        attribute: "cumulatedEarnedUnits",
      },
    ],
    [message, currency],
  );

  const findMatchingCondition = useCallback(
    (item: { attribute: string }) => {
      return tierSetConditions?.find(
        (condition) => condition.attribute === item.attribute,
      );
    },
    [tierSetConditions],
  );

  return (
    <OLPaper className={classes.OLPaper}>
      <FieldArray
        name="conditions"
        render={({ remove, push, replace, form: { values } }) => {
          const { conditions }: FETierSetFormat = values;
          const areConditionsAdded = conditions && conditions.length > 0;
          const tierSetConditionsItems = [
            ...tierSetConditions?.map((item) => ({
              ...item,
              onClick: () => {
                push({
                  attribute: item.attribute,
                });
              },
            })),
          ];
          const isTierSetMigrated = values.isMigrated === true;

          return (
            <>
              {areConditionsAdded ? (
                <>
                  {conditions.map(
                    (item: { attribute: string }, index: number) => {
                      const isAndOperatorVisible =
                        index + 1 < conditions.length;

                      return (
                        <Condition
                          key={index}
                          condition={item}
                          index={index}
                          initialValue={{
                            id: item?.attribute,
                            label: findMatchingCondition(item)?.label as string,
                          }}
                          isAndOperatorVisible={isAndOperatorVisible}
                          tierSetConditions={tierSetConditions}
                          remove={remove}
                          replace={replace}
                        />
                      );
                    },
                  )}
                  <Grid item xs={12} mt={8}>
                    <OLMenuButton
                      buttonVariant="contained"
                      data-test-id="add-condition-btn"
                      items={customEvents && tierSetConditionsItems}
                      placeholder={message({ id: "typeCondition" })}
                      submit={message({ id: "addCondition" })}
                      disabled={isTierSetMigrated && conditions.length >= 1}
                    />
                  </Grid>
                </>
              ) : (
                <>
                  <Box className={classes.conditionsContainer}>
                    <Box className={classes.iconWrapper}>
                      <FilePlus03Icon className={classes.icon} />
                    </Box>
                    <Box className={classes.textContainer}>
                      <Typography className={classes.title}>
                        {message({ id: "chooseConditionsForThisSet" })}
                      </Typography>
                      <Typography className={classes.description}>
                        {message({ id: "chooseConditionsInfo" })}
                      </Typography>
                    </Box>
                    <OLMenuButton
                      buttonVariant="contained"
                      data-test-id="add-condition-btn"
                      items={customEvents && tierSetConditionsItems}
                      placeholder={message({ id: "typeCondition" })}
                      submit={message({ id: "addFirstCondition" })}
                    />
                  </Box>
                </>
              )}
            </>
          );
        }}
      />
    </OLPaper>
  );
});
